import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { getCreatedActivities, deleteActivity } from "@/services/api/activity";
import type { ActivityInfo } from "@/services/api/activity/types";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import StudentActivityCard from "@/components/student/StudentActivityCard";

const PAGE_SIZE = 10;

const BusinessActivitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<ActivityInfo[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const fetchPage = useCallback(
    async (targetPage: number, mode: "initial" | "more") => {
      if (mode === "more") setLoadingMore(true);
      else setLoading(true);

      const resp = await getCreatedActivities(targetPage);

      if (mode === "more") setLoadingMore(false);
      setLoading(false);

      const result = resp?.result;
      if (resp?.status !== 0 || !Array.isArray(result)) {
        setError(true);
        setHasMore(false);
        return;
      }

      setError(false);
      setPage(targetPage);
      setHasMore(result.length >= PAGE_SIZE);
      setActivities((prev) => (mode === "more" ? [...prev, ...result] : result));
    },
    [],
  );

  useEffect(() => {
    fetchPage(1, "initial");
  }, [fetchPage]);

  const handleDelete = async (activityId: string) => {
    if (!confirm(t("Are you sure you want to delete this activity?"))) return;
    const resp = await deleteActivity(activityId);
    if (resp?.status === 0) {
      setActivities((prev) => prev.filter((a) => a.id !== activityId));
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
            <Compass size={22} />
          </span>
          <h1 className="text-2xl font-bold text-gray-900">{t("Activities")}</h1>
        </div>
        <Link
          to="/portal/activities/new"
          className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#e08f0e]"
        >
          <Plus size={16} />
          {t("Create Activity")}
        </Link>
      </div>

      <div className="mt-4">
        {loading ? (
          <AdminListSkeleton count={3} />
        ) : error ? (
          <div className="rounded-2xl border border-gray-100 bg-white">
            <AdminEmptyState
              iconType="MaterialCommunityIcons"
              iconName="cloud-alert-outline"
              title={t("Something went wrong")}
              message={t("Pull to refresh to load activities.")}
              actionLabel={t("Retry")}
              onAction={() => fetchPage(1, "initial")}
            />
          </div>
        ) : activities.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white">
            <AdminEmptyState
              iconType="MaterialCommunityIcons"
              iconName="calendar-blank-outline"
              title={t("No activities yet")}
              message={t("Activities you create will appear here.")}
            />
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {activities.map((activity) => (
                <div key={activity.id} className="relative">
                  <StudentActivityCard activity={activity} basePath="/portal/activities" />
                  <div className="absolute right-3 bottom-3 flex gap-2">
                    <Link
                      to={`/portal/activities/${activity.id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
                    >
                      {t("Manage")}
                    </Link>
                    <Link
                      to={`/portal/activities/${activity.id}/edit`}
                      state={{ activity }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
                    >
                      <Pencil size={13} />
                      {t("Edit")}
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(activity.id)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 transition hover:border-red-500 hover:text-red-500"
                    >
                      <Trash2 size={13} />
                      {t("Delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => fetchPage(page + 1, "more")}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingMore && <Loader2 size={16} className="animate-spin" />}
                  {t("Load More")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BusinessActivitiesPage;
